import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@clearpath/design-system/ui/alert-dialog';
import { Checkbox } from '../ui/checkbox.js';
import { Banner } from './Banner.js';
import { Spinner } from './Spinner';

// =============================================================================
// ConfirmDialog + useConfirm() — promise-based replacement for window.confirm.
//
// Provides an imperative `confirm(options)` that returns Promise<boolean>,
// backed by shadcn AlertDialog. Supports destructive vs default intent,
// cascade preview (list of dependent items that will also be affected),
// and async busy state during the confirmation callback.
//
// Wire <ConfirmProvider> once at the app root (main.tsx) and call
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete role?', intent: 'destructive' })) { ... }
//
// V2-QUALITY-PLAN §3.5: every destructive click goes through AlertDialog.
// =============================================================================

export type ConfirmIntent = 'destructive' | 'default';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  /** Primary action label. Default: "Confirm" (or "Delete" when destructive). */
  confirmLabel?: string;
  cancelLabel?: string;
  intent?: ConfirmIntent;
  /**
   * Optional cascade preview — e.g. "this will also release 3 assignments
   * and archive 2 files". Each item renders as a bullet under the
   * description.
   */
  cascade?: readonly string[];
  /**
   * ENG-656 - render the confirm action disabled. Used by delete dialogs
   * whose preflight found blocking references: the dialog lists them (via
   * `cascade`) and the user can only Cancel, instead of clicking Delete and
   * learning about the refusal from an easy-to-miss toast. Advisory UX only
   * - the server keeps its own authoritative check either way.
   */
  confirmDisabled?: boolean;
  /**
   * ENG-656 - render `description` + `cascade` inside a semantic Banner
   * (icon + tinted surface) instead of the muted dialog text. Requires a
   * string `description` (it becomes the Banner title); a ReactNode
   * description falls back to the plain rendering. Used by
   * blockedDeleteConfirmProps so a blocked delete reads as a warning, not
   * as ordinary helper text.
   */
  tone?: 'warning' | 'error';
  /**
   * ENG-1811 - require an explicit acknowledgement before the confirm action
   * unlocks. When set, a checkbox with this label renders above the footer and
   * the confirm button stays disabled until it is ticked. Used for consequential,
   * hard-to-reverse actions (e.g. releasing a field's small-group privacy
   * protection) where a single click is too easy. Advisory UX only - the server
   * keeps its own authoritative check either way. Composes with `confirmDisabled`
   * and `tone`: a warning-toned release dialog can show a banner AND gate on the
   * acknowledgement together. Backward-compatible: absent, no checkbox renders and
   * the confirm button's enabled state is unchanged.
   */
  acknowledgeLabel?: string;
  /**
   * Work to run on confirm, with the dialog held open and busy until it
   * settles. Without this the dialog closes the instant Confirm is clicked and
   * the user watches an already-dismissed screen do the work — which for a
   * destructive action reads as "did that go through?".
   *
   * `confirm()` still resolves true once it completes, false on cancel. If the
   * action throws, the dialog closes and the rejection propagates to the
   * caller's await, so the caller reports the failure as it normally would.
   */
  action?: () => Promise<unknown> | unknown;
}

interface ConfirmApi {
  confirm(options: ConfirmOptions): Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmApi | null>(null);

interface PendingState extends ConfirmOptions {
  resolve: (accepted: boolean) => void;
  reject: (error: unknown) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);
  const [busy, setBusy] = useState(false);
  // ENG-1811 - acknowledgement checkbox state. Reset to false on every new
  // dialog and on close, so a prior confirmation's tick never carries over to
  // the next (which would silently pre-unlock a consequential action).
  const [acknowledged, setAcknowledged] = useState(false);

  const api = useMemo<ConfirmApi>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve, reject) => {
          setAcknowledged(false);
          setPending({ ...options, resolve, reject });
        }),
    }),
    [],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Escape / click-outside must not dismiss a dialog whose action is
      // mid-flight — the work would finish against a closed dialog.
      if (busy) return;
      if (!open && pending) {
        // User dismissed via Escape / click-outside → treated as cancel.
        pending.resolve(false);
        setPending(null);
        setAcknowledged(false);
      }
    },
    [pending, busy],
  );

  const handleCancel = useCallback(() => {
    if (!pending) return;
    pending.resolve(false);
    setPending(null);
    setAcknowledged(false);
  }, [pending]);

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    if (!pending.action) {
      pending.resolve(true);
      setPending(null);
      setAcknowledged(false);
      return;
    }
    setBusy(true);
    try {
      await pending.action();
      pending.resolve(true);
    } catch (error) {
      pending.reject(error);
    } finally {
      setBusy(false);
      setPending(null);
      setAcknowledged(false);
    }
  }, [pending]);

  const destructive = pending?.intent === 'destructive';
  const confirmLabel =
    pending?.confirmLabel ?? (destructive ? 'Delete' : 'Confirm');
  // Banner mode (ENG-656): tone set + string description -> the explanation
  // and cascade render as one semantic callout instead of muted dialog text.
  const bannerMode = !!pending?.tone && typeof pending?.description === 'string';

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={onOpenChange}>
        {pending && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{pending.title}</AlertDialogTitle>
              {pending.description && !bannerMode && (
                <AlertDialogDescription>{pending.description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            {bannerMode ? (
              // Keep Radix's aria-describedby wiring by rendering the Banner
              // inside the (asChild) Description element.
              <AlertDialogDescription asChild>
                <div className="text-left">
                  <Banner
                    tone={pending.tone!}
                    title={pending.description as string}
                    description={
                      pending.cascade && pending.cascade.length > 0 ? (
                        <ul className="text-xs space-y-1 list-disc pl-5">
                          {pending.cascade.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      ) : undefined
                    }
                  />
                </div>
              </AlertDialogDescription>
            ) : (
              pending.cascade &&
              pending.cascade.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
                  {pending.cascade.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )
            )}
            {/* ENG-1811 - acknowledgement gate. The confirm button stays disabled
                until this is ticked, so a consequential action can't be a single
                mis-click. A real label + control (not raw markup) keyed to the
                checkbox for click-target + a11y. */}
            {pending.acknowledgeLabel && (
              <label className="mt-1 flex cursor-pointer items-start gap-2 rounded-md border border-border bg-bg-sunken p-3 text-left text-sm text-foreground">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(value) => setAcknowledged(value === true)}
                  disabled={busy}
                  className="mt-0.5"
                  aria-label={pending.acknowledgeLabel}
                  data-testid="confirm-acknowledge"
                />
                <span>{pending.acknowledgeLabel}</span>
              </label>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancel} disabled={busy}>
                {pending.cancelLabel ?? 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                // Radix closes the dialog on its own click; an action that
                // holds it open has to suppress that and close on settle.
                onClick={(event) => {
                  if (pending.action) event.preventDefault();
                  void handleConfirm();
                }}
                disabled={
                  pending.confirmDisabled ||
                  busy ||
                  (!!pending.acknowledgeLabel && !acknowledged)
                }
                className={
                  destructive
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : undefined
                }
              >
                {confirmLabel}
                {busy && <Spinner size="sm" className="ml-2" />}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/** Throws if called outside ConfirmProvider — fail loudly at mount. */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() must be used inside <ConfirmProvider>');
  }
  return ctx.confirm;
}

/**
 * Pseudo-hook wrapping useConfirm for the common destructive-delete case.
 * `await confirmDelete('Delete role "Admin"?')` returns true/false.
 */
export function useConfirmDelete() {
  const confirm = useConfirm();
  return (title: string, options: Omit<ConfirmOptions, 'title' | 'intent'> = {}) =>
    confirm({ title, intent: 'destructive', ...options });
}

/** Inline spinner next to the confirm button while the parent callback awaits. */
export function ConfirmBusySpinner() {
  return <Spinner size="sm" className="ml-2" />;
}
